import { z } from 'zod';

/**
 * Contrato das imagens de aplicação guardadas no bucket S3/MinIO.
 *
 * Vive em `packages/shared` porque as MESMAS regras valem em três lugares:
 *  1. `apps/api/src/lib/s3.ts` — gera a chave no upload e valida antes de
 *     ler/apagar objeto no bucket;
 *  2. `apps/api/src/routes/uploads.ts` — valida o `:key` recebido na URL
 *     (anti path traversal / leitura de outros prefixos do bucket);
 *  3. `schemas/applications.ts` — valida `applications.image_key` na escrita
 *     (o registro só aceita chave que a própria API gerou).
 *
 * A coluna guarda SÓ A CHAVE (`applications/<uuid>.<ext>`), nunca URL absoluta:
 * endpoint e bucket mudam entre dev/UAT/PRD e o registro não pode amarrá-los.
 */

/** Prefixo único das imagens de aplicação no bucket. */
export const APPLICATION_IMAGE_PREFIX = 'applications/';

/** Teto de 4 MB por imagem (contrato do POST /api/uploads/application-image). */
export const APPLICATION_IMAGE_MAX_BYTES = 4 * 1024 * 1024;

/**
 * Content-types aceitos → extensão da chave. PNG/JPEG/WebP cobrem screenshot e
 * logo; SVG fica FORA de propósito (é executável no browser: XSS via
 * `<script>` embutido, mesmo servido com Content-Type de imagem).
 */
export const APPLICATION_IMAGE_CONTENT_TYPES = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
} as const;

export type ApplicationImageContentType = keyof typeof APPLICATION_IMAGE_CONTENT_TYPES;

export function isApplicationImageContentType(value: string): value is ApplicationImageContentType {
  return value in APPLICATION_IMAGE_CONTENT_TYPES;
}

/**
 * Padrão RESTRITO da chave: `applications/<uuid v4 minúsculo>.<png|jpg|jpeg|webp>`.
 * É a defesa contra path traversal e contra leitura de outros prefixos do
 * bucket — a chave é gerada pelo servidor, mas o `:key` da URL chega do cliente
 * e é validado contra este padrão ANTES de qualquer chamada ao S3.
 *
 * Sem `/g` e sem `/i` de propósito: `lastIndex` de regex global vaza estado
 * entre chamadas de `.test()` e maiúscula não aparece em UUID gerado por
 * `crypto.randomUUID()`.
 */
export const APPLICATION_IMAGE_KEY_PATTERN = /^applications\/[a-f0-9-]{36}\.(png|jpe?g|webp)$/;

export function isApplicationImageKey(value: unknown): value is string {
  return typeof value === 'string' && APPLICATION_IMAGE_KEY_PATTERN.test(value);
}

/**
 * VARIANTES de thumb: para cada original `applications/<uuid>.<ext>` a API
 * grava `applications/<uuid>@<largura>.webp` nas larguras abaixo. A chave do
 * original é a única que vai para o banco (`image_key`/`galeria_keys`); as
 * variantes são derivadas dela e pedidas na leitura com `?w=<largura>`.
 *
 *  - 320  → miniatura (avatar do card, thumbs da ficha);
 *  - 640  → hero do card do catálogo (≈300–400 px de largura, telas retina);
 *  - 1280 → slide do deck e visualizador da ficha.
 *
 * O original comprimido continua disponível sem `w` (download, impressão).
 */
export const APPLICATION_IMAGE_VARIANT_WIDTHS = [320, 640, 1280] as const;
export type ApplicationImageVariantWidth = (typeof APPLICATION_IMAGE_VARIANT_WIDTHS)[number];

/** Largura máxima do ORIGINAL guardado: acima disto a API reduz no upload. */
export const APPLICATION_IMAGE_MAX_WIDTH = 1920;

export function isApplicationImageVariantWidth(
  value: unknown,
): value is ApplicationImageVariantWidth {
  return (
    typeof value === 'number' &&
    (APPLICATION_IMAGE_VARIANT_WIDTHS as ReadonlyArray<number>).includes(value)
  );
}

/** Chave da variante WebP de `key` na largura `w` — `applications/<uuid>@320.webp`. */
export function applicationImageVariantKey(key: string, w: ApplicationImageVariantWidth): string {
  return `${key.replace(/\.(png|jpe?g|webp)$/, '')}@${w}.webp`;
}

/** Todas as chaves de variante de um original (para apagar junto com ele). */
export function applicationImageVariantKeys(key: string): string[] {
  return APPLICATION_IMAGE_VARIANT_WIDTHS.map((w) => applicationImageVariantKey(key, w));
}

/** Zod da chave — usado no `imageKey` do payload de criação/edição da app. */
export const applicationImageKeySchema = z
  .string()
  .regex(APPLICATION_IMAGE_KEY_PATTERN, 'Chave de imagem inválida');

/**
 * Teto de imagens POR APLICAÇÃO, contando a capa: capa + 7 telas.
 *
 * Decisão do dono do produto. O número serve a dois senhores: uma fala de um a
 * dois minutos por app no deck não sustenta mais que isso, e 8 × 4 MB põe o pior
 * caso em 32 MB por aplicação — com 44 apps, um teto de bucket de ~1,4 GB.
 *
 * O teto é cobrado no UPLOAD, não só na publicação: cobrar só ao publicar
 * deixaria os objetos já gravados no bucket antes da recusa, e aí o usuário que
 * subiu 12 imagens teria 12 objetos órfãos e um formulário que não salva.
 */
export const APPLICATION_MAX_IMAGES = 8;
